import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, CheckCircleOutlined, ExceptionOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { useImpactAction, useTmmdeImpactAssessment } from '@/hooks/useCalibration';
import { calibrationService, type TmmdeImpactDisposition, type TmmdeImpactItem } from '@/services/calibration.service';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_TMMDE_IMPACT_ASSESSMENT } from '@/constants/document-entity-types';
import { formatDate, formatDateTime } from '@/utils/format/date';
import {
  IMPACT_DISPOSITION_META,
  IMPACT_ITEM_TYPE_LABEL,
  IMPACT_TRIGGER_LABEL,
  impactItemLink,
  tmmdeStatusColor,
  tmmdeStatusLabel,
} from './tmmdeDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DISPOSITION_CHOICES: Array<{ value: Exclude<TmmdeImpactDisposition, 'PENDING'>; label: string; hint: string }> = [
  { value: 'NO_IMPACT', label: 'No impact', hint: 'Result not affected (e.g. outside the error, other evidence)' },
  { value: 'REINSPECT', label: 'Re-inspect', hint: 'Repeat the inspection with calibrated equipment' },
  { value: 'NOTIFY_CUSTOMER', label: 'Notify customer', hint: 'Product delivered — the customer must be told' },
];

const SEVERITY_OPTIONS = ['Critical', 'Major', 'Minor'].map((v) => ({ value: v, label: v }));

const TmmdeImpactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(user?.is_super_admin || user?.permissions?.includes('calibration.impact.manage'));

  const { data, isLoading, error } = useTmmdeImpactAssessment(id);
  const [summary, setSummary] = useState('');
  const [dispItem, setDispItem] = useState<TmmdeImpactItem | null>(null);
  const [notifyItem, setNotifyItem] = useState<TmmdeImpactItem | null>(null);
  const [ncrTarget, setNcrTarget] = useState<{ item?: TmmdeImpactItem } | null>(null);
  const [closing, setClosing] = useState(false);
  const [dispForm] = Form.useForm();
  const [notifyForm] = Form.useForm();
  const [ncrForm] = Form.useForm();
  const [closeForm] = Form.useForm();

  useEffect(() => {
    setSummary(data?.summary ?? '');
  }, [data?.summary]);

  const summaryMutation = useImpactAction((s: string) => calibrationService.updateImpactSummary(id!, s || null), 'Assessment saved');
  const dispositionMutation = useImpactAction(
    (v: { itemId: string; disposition: TmmdeImpactDisposition; notes?: string }) =>
      calibrationService.dispositionImpactItem(id!, v.itemId, { disposition: v.disposition, notes: v.notes }),
    'Disposition recorded'
  );
  const notifyMutation = useImpactAction(
    (v: { itemId: string; notified_at: string; contact: string; notes?: string }) =>
      calibrationService.recordImpactNotification(id!, v.itemId, v),
    'Customer notification recorded'
  );
  const ncrMutation = useImpactAction(
    (v: { item_id?: string; severity: string; description?: string }) => calibrationService.raiseImpactNcr(id!, v),
    (res: { ncr_number: string }) => `NCR ${res.ncr_number} raised`
  );
  const refreshMutation = useImpactAction(() => calibrationService.refreshImpactAssessment(id!), 'Affected items refreshed');
  const closeMutation = useImpactAction(
    (notes: string) => calibrationService.closeImpactAssessment(id!, notes),
    'Impact assessment closed'
  );

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/tmmde/impact')}>
          Impact assessments
        </Button>
        <Alert type="error" showIcon style={{ marginTop: 16 }} message="Impact assessment not found" />
      </div>
    );
  }

  const open = data.status === 'OPEN';
  const editable = open && canManage;
  const items = data.items ?? [];
  const pending = items.filter((i) => i.disposition === 'PENDING').length;
  const awaitingNotice = items.filter((i) => i.disposition === 'NOTIFY_CUSTOMER' && !i.notified_at).length;

  const columns: ColumnsType<TmmdeImpactItem> = [
    {
      title: 'Type',
      dataIndex: 'item_type',
      width: 130,
      render: (v: TmmdeImpactItem['item_type']) => IMPACT_ITEM_TYPE_LABEL[v] || v,
    },
    {
      title: 'Affected',
      key: 'label',
      render: (_: unknown, i) => {
        const link = impactItemLink(i);
        return (
          <Space direction="vertical" size={0}>
            {link ? (
              <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(link)}>
                {i.label}
              </Button>
            ) : (
              <span>{i.label}</span>
            )}
            {i.item_type === 'INSPECTION' && (i.work_order_number || i.customer_name) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[i.work_order_number, i.customer_name].filter(Boolean).join(' · ')}
              </Text>
            )}
            {i.item_type === 'DELIVERY_NOTE' && i.customer_name && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {i.customer_name}
              </Text>
            )}
            {Number(i.override_used) ? <Tag color="red">Used under override</Tag> : null}
          </Space>
        );
      },
    },
    { title: 'Used', dataIndex: 'used_at', width: 160, render: (v) => (v ? formatDateTime(v) : '—') },
    {
      title: 'Disposition',
      key: 'disposition',
      width: 260,
      render: (_: unknown, i) => (
        <Space direction="vertical" size={0}>
          <Tag color={IMPACT_DISPOSITION_META[i.disposition].color}>{IMPACT_DISPOSITION_META[i.disposition].label}</Tag>
          {i.disposition_notes && <Text style={{ fontSize: 12 }}>{i.disposition_notes}</Text>}
          {i.dispositioned_by_name && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {i.dispositioned_by_name} · {formatDateTime(i.dispositioned_at)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Customer notification',
      key: 'notification',
      width: 220,
      render: (_: unknown, i) =>
        i.item_type !== 'CUSTOMER' && i.item_type !== 'DELIVERY_NOTE' ? (
          <Text type="secondary">—</Text>
        ) : i.notified_at ? (
          <Space direction="vertical" size={0}>
            <span>
              {formatDate(i.notified_at)} · {i.notified_contact}
            </span>
            {i.notification_notes && <Text style={{ fontSize: 12 }}>{i.notification_notes}</Text>}
            {i.notified_by_name && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Recorded by {i.notified_by_name}
              </Text>
            )}
          </Space>
        ) : i.disposition === 'NOTIFY_CUSTOMER' ? (
          <Text type="danger">Not yet recorded</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'NCR',
      dataIndex: 'ncr_number',
      width: 120,
      render: (v: string, i) =>
        v ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/ncrs/${i.ncr_id}`)}>
            {v}
          </Button>
        ) : (
          '—'
        ),
    },
    ...(editable
      ? [
          {
            title: '',
            key: 'actions',
            width: 230,
            render: (_: unknown, i: TmmdeImpactItem) => (
              <Space wrap size={4}>
                <Button
                  size="small"
                  onClick={() => {
                    dispForm.setFieldsValue({
                      disposition: i.disposition === 'PENDING' ? undefined : i.disposition,
                      notes: i.disposition_notes ?? '',
                    });
                    setDispItem(i);
                  }}
                >
                  Disposition
                </Button>
                {(i.item_type === 'CUSTOMER' || i.item_type === 'DELIVERY_NOTE') && (
                  <Button
                    size="small"
                    onClick={() => {
                      notifyForm.setFieldsValue({
                        notified_at: i.notified_at ? dayjs(i.notified_at) : dayjs(),
                        contact: i.notified_contact ?? '',
                        notes: i.notification_notes ?? '',
                      });
                      setNotifyItem(i);
                    }}
                  >
                    Notification
                  </Button>
                )}
                {!i.ncr_id && (i.item_type === 'INSPECTION' || i.item_type === 'WORK_ORDER') && (
                  <Button
                    size="small"
                    onClick={() => {
                      ncrForm.setFieldsValue({ severity: 'Major', description: '' });
                      setNcrTarget({ item: i });
                    }}
                  >
                    NCR
                  </Button>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/tmmde/impact')} style={{ marginBottom: 8 }}>
        Impact assessments
      </Button>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space align="center" wrap>
          <Title level={2} style={{ margin: 0 }}>
            Impact assessment {data.assessment_number}
          </Title>
          <Tag color={open ? 'orange' : 'green'}>{open ? 'Open' : 'Closed'}</Tag>
        </Space>
        {editable && (
          <Space wrap>
            <Button icon={<ReloadOutlined />} loading={refreshMutation.isPending} onClick={() => refreshMutation.mutate(undefined)}>
              Refresh affected items
            </Button>
            {!data.ncr_id && (
              <Button
                icon={<ExceptionOutlined />}
                onClick={() => {
                  ncrForm.setFieldsValue({ severity: 'Major', description: '' });
                  setNcrTarget({});
                }}
              >
                Raise NCR
              </Button>
            )}
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={pending > 0 || awaitingNotice > 0}
              onClick={() => {
                closeForm.resetFields();
                setClosing(true);
              }}
            >
              Close assessment
            </Button>
          </Space>
        )}
      </Space>

      {open && (pending > 0 || awaitingNotice > 0) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={[
            pending ? `${pending} affected item(s) still need a disposition` : null,
            awaitingNotice ? `${awaitingNotice} customer notification(s) not yet recorded` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, md: 2, xl: 3 }} size="small">
          <Descriptions.Item label="Equipment">
            <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/qms/tmmde/${data.asset_id}`)}>
              {data.asset_code} — {data.asset_name}
            </Button>
          </Descriptions.Item>
          <Descriptions.Item label="Serial no.">{data.serial_number || '—'}</Descriptions.Item>
          <Descriptions.Item label="Current status">
            <Tag color={tmmdeStatusColor(data.calibration_status)}>{tmmdeStatusLabel(data.calibration_status)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Trigger">{IMPACT_TRIGGER_LABEL[data.trigger_type] || data.trigger_type}</Descriptions.Item>
          <Descriptions.Item label="Reason" span={2}>
            {data.trigger_reason || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Affected period">
            {formatDate(data.period_from, { nullDisplay: 'First use' })} – {formatDate(data.period_to)}
          </Descriptions.Item>
          <Descriptions.Item label="Opened">
            {formatDateTime(data.created_at)}
            {data.created_by_name ? ` · ${data.created_by_name}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="NCR">
            {data.ncr_number ? (
              <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/qms/ncrs/${data.ncr_id}`)}>
                {data.ncr_number}
              </Button>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          {!open && (
            <>
              <Descriptions.Item label="Closed">
                {formatDateTime(data.closed_at)}
                {data.closed_by_name ? ` · ${data.closed_by_name}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Closure notes" span={2}>
                {data.closure_notes || '—'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      <Card title="Assessment" style={{ marginBottom: 16 }}>
        <TextArea
          rows={3}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          readOnly={!editable}
          placeholder="How far out of tolerance was the equipment, and what does that mean for results obtained with it?"
        />
        {editable && (
          <Button
            style={{ marginTop: 8 }}
            disabled={summary === (data.summary ?? '')}
            loading={summaryMutation.isPending}
            onClick={() => summaryMutation.mutate(summary)}
          >
            Save assessment
          </Button>
        )}
      </Card>

      <Card title={`Affected items (${items.length})`} style={{ marginBottom: 16 }}>
        <Table rowKey="id" size="small" columns={columns} dataSource={items} pagination={false} scroll={{ x: 1300 }} />
      </Card>

      <EntityAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_TMMDE_IMPACT_ASSESSMENT}
        entityId={data.id}
        title="Evidence (customer notifications, re-inspection reports)"
        readOnly={!editable}
        queryKeyPrefix={['documents', 'tmmde-impact-assessment']}
      />

      <Modal
        open={Boolean(dispItem)}
        title={dispItem ? `Disposition: ${dispItem.label}` : ''}
        okText="Save disposition"
        confirmLoading={dispositionMutation.isPending}
        onCancel={() => setDispItem(null)}
        onOk={async () => {
          const v = await dispForm.validateFields();
          await dispositionMutation.mutateAsync({ itemId: dispItem!.id, disposition: v.disposition, notes: v.notes });
          setDispItem(null);
        }}
        destroyOnHidden
      >
        <Form form={dispForm} layout="vertical">
          <Form.Item name="disposition" label="Decision" rules={[{ required: true, message: 'Choose a decision' }]}>
            <Radio.Group>
              <Space direction="vertical">
                {DISPOSITION_CHOICES.map((c) => (
                  <Radio key={c.value} value={c.value}>
                    {c.label} <Text type="secondary">— {c.hint}</Text>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(a, b) => a.disposition !== b.disposition}
          >
            {({ getFieldValue }) => (
              <Form.Item
                name="notes"
                label="Justification / action"
                rules={[
                  {
                    required: getFieldValue('disposition') && getFieldValue('disposition') !== 'NO_IMPACT',
                    message: 'Notes are required for re-inspection or customer notification',
                  },
                ]}
              >
                <TextArea rows={3} />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={Boolean(notifyItem)}
        title={notifyItem ? `Customer notification: ${notifyItem.customer_name || notifyItem.label}` : ''}
        okText="Record notification"
        confirmLoading={notifyMutation.isPending}
        onCancel={() => setNotifyItem(null)}
        onOk={async () => {
          const v = (await notifyForm.validateFields()) as { notified_at: Dayjs; contact: string; notes?: string };
          await notifyMutation.mutateAsync({
            itemId: notifyItem!.id,
            notified_at: v.notified_at.format('YYYY-MM-DD'),
            contact: v.contact,
            notes: v.notes,
          });
          setNotifyItem(null);
        }}
        destroyOnHidden
      >
        <Form form={notifyForm} layout="vertical">
          <Form.Item name="notified_at" label="Date notified" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="contact" label="Customer contact" rules={[{ required: true, message: 'Who was told?' }]}>
            <Input placeholder="Name, role, email or phone" maxLength={255} />
          </Form.Item>
          <Form.Item name="notes" label="What was communicated">
            <TextArea rows={3} />
          </Form.Item>
          <Text type="secondary">Attach the email or letter under Evidence on this page.</Text>
        </Form>
      </Modal>

      <Modal
        open={Boolean(ncrTarget)}
        title={ncrTarget?.item ? `Raise NCR for ${ncrTarget.item.label}` : 'Raise NCR for this assessment'}
        okText="Raise NCR"
        confirmLoading={ncrMutation.isPending}
        onCancel={() => setNcrTarget(null)}
        onOk={async () => {
          const v = await ncrForm.validateFields();
          await ncrMutation.mutateAsync({
            item_id: ncrTarget?.item?.id,
            severity: v.severity,
            description: v.description || undefined,
          });
          setNcrTarget(null);
        }}
        destroyOnHidden
      >
        <Form form={ncrForm} layout="vertical">
          <Form.Item name="severity" label="Severity" rules={[{ required: true }]}>
            <Select options={SEVERITY_OPTIONS} />
          </Form.Item>
          <Form.Item name="description" label="Description" extra="Leave blank to use a description generated from this assessment.">
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={closing}
        title="Close impact assessment"
        okText="Close assessment"
        confirmLoading={closeMutation.isPending}
        onCancel={() => setClosing(false)}
        onOk={async () => {
          const v = await closeForm.validateFields();
          await closeMutation.mutateAsync(v.closure_notes);
          setClosing(false);
        }}
        destroyOnHidden
      >
        <Form form={closeForm} layout="vertical">
          <Form.Item name="closure_notes" label="Closure notes" rules={[{ required: true, message: 'Closure notes are required' }]}>
            <TextArea rows={4} placeholder="Conclusion, re-inspection outcome, customer responses" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TmmdeImpactDetailPage;
